import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';

@Injectable()
export class PaymentService {
  constructor(private http: HttpClient, private jwtTokenService: JWTTokenService) { }

  public checkCoupon(couponCode: string): Observable<boolean> {
    return this.http.get<boolean>(getUrl(`check-coupon/${couponCode}`), this.requestOptionsAuthenticated());
  }

  public authorizePayment(purchase: PaymentInfo): Observable<PaymentAuthorization> {
    if (purchase.totalAmount === 0) {
      return of({ amount: 0, token: null });
    } else {
      return new Observable<PaymentAuthorization>(subscriber => {
        // process payment via Stripe
        const taxFormatted = (purchase.taxAmount / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const taxAmountFormatted = ((purchase.totalAmount + purchase.taxAmount) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const totalPrice = '$' + taxAmountFormatted + ' ($' + taxFormatted + ' Tax)';
        const stripe = (<any>window).StripeCheckout;
        stripe
          .configure({
            key: purchase.publicStripeKey,
            locale: 'auto',
            token: (token: any) => {
              subscriber.next({ token: token.id, amount: purchase.totalAmount + purchase.taxAmount });
              subscriber.complete();
            }
          })
          .open({
            name: totalPrice,
            description: purchase.description,
            zipCode: false,
            currency: 'usd',
            amount: purchase.totalAmount + purchase.taxAmount,
            panelLabel: 'Confirm: ',
            allowRememberMe: true
          });
      });
    }
  }

  public getPurchases() {
    return this.http.get<any>(getUrl('purchase/get-all'), this.requestOptionsAuthenticated());
  }

  public generateReport(reportParameters: any) {
    return this.http.post<any>(getUrl('purchase/report'), reportParameters, this.requestOptionsAuthenticated());
  }

  public refundPurchase(purchaseId: number) {
    return this.http.get<boolean>(getUrl('purchase/refund/' + purchaseId), this.requestOptionsAuthenticated());
  }

  public getPurchaseDetails(purchaseId: number) {
    return this.http.get<any>(getUrl('purchase/details/' + purchaseId), this.requestOptionsAuthenticated());
  }

  public getClientProductPrices() {
    return this.http.get<any>(getUrl('price/get-client-product-prices'), this.requestOptionsAuthenticated());
  }

  public updateProductPrices(clientProductPrice: ClientTypeProductPrice) {
    return this.http.post(getUrl('price/update-client-product-prices'), clientProductPrice, this.requestOptionsAuthenticated());
  }

  private requestOptionsAuthenticated() {
    const requestOptions = {
      headers: new HttpHeaders().set('Content-type', 'application/json').append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal'))
    };
    return requestOptions;
  }
}

export interface PaymentAuthorization {
  token: string;
  amount: number;
}

export interface PreparedPurchaseInfo {
  publicStripeKey: string;
  purchaseId: number;
  totalAmount: number;
  totalTax: number;
  couponValid: boolean;
}

export interface ClientTypeProductPrice {
  productId: number;
  clientTypeId: number;
  price: number;
}

export enum productCodes {
  LightShow1Day = 'LightShow1Day',
  SelfieCam1Day = 'SelfieCam1Day',
  TriviaGame1Day = 'TriviaGame1Day',
  LightShowCustomTrack = 'LightShowCustomTrack',
  LightShowCustomVideo = 'LightShowCustomVideo',
  LightShowTrackUpdate = 'LightShowTrackUpdate'
}

export interface PaymentInfo {
  description: any;
  taxAmount: number;
  totalAmount: number;
  publicStripeKey: any;
}
